import { useEffect, useRef } from 'react';
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useQuery } from '@tanstack/react-query';
import { fetchWind } from '../lib/api';
import { lightBasemapSource } from '../lib/basemap';
import { colorForMph, WindParticleSystem } from '../lib/windParticles';

export default function WindMap({
  centerLat,
  centerLon,
}: {
  centerLat: number;
  centerLon: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const windSystemRef = useRef<WindParticleSystem | null>(null);

  const wind = useQuery({
    queryKey: ['wind'],
    queryFn: fetchWind,
    refetchInterval: 30 * 60 * 1000,
  });

  // Init map (same light basemap as radar map).
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: {
        version: 8,
        sources: {
          basemap: lightBasemapSource,
        },
        layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }],
      },
      center: [centerLon, centerLat],
      zoom: 10,
      attributionControl: { compact: true },
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [centerLat, centerLon]);

  // Wind particle overlay — same technique as the radar-map version,
  // but here it's the only thing on the map. Particles live in lon/lat
  // and re-project to pixels each frame so pan/zoom stays anchored.
  useEffect(() => {
    const map = mapRef.current;
    const canvas = canvasRef.current;
    if (!map || !canvas || !wind.data) return;

    const resize = () => {
      const rect = map.getContainer().getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const project = (lon: number, lat: number): [number, number] | null => {
      const p = map.project([lon, lat]);
      return [p.x, p.y];
    };

    const system = new WindParticleSystem(canvas, wind.data, { projectToPixel: project });
    windSystemRef.current = system;
    system.start();

    const clearOnMove = () => system.clear();
    map.on('movestart', clearOnMove);
    map.on('zoomstart', clearOnMove);
    window.addEventListener('resize', resize);

    return () => {
      system.stop();
      windSystemRef.current = null;
      map.off('movestart', clearOnMove);
      map.off('zoomstart', clearOnMove);
      window.removeEventListener('resize', resize);
    };
  }, [wind.data]);

  const updated = wind.data?.time
    ? new Date(wind.data.time).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs uppercase tracking-wider opacity-70">Wind</h2>
        <span className="text-[10px] opacity-60">Updated {updated}</span>
      </div>
      <div className="rounded-lg overflow-hidden bg-slate-900 border border-white/10">
        <div className="relative h-80 w-full">
          <div ref={containerRef} className="absolute inset-0" />
          <canvas
            ref={canvasRef}
            className="absolute inset-0 pointer-events-none"
          />
          {wind.isLoading && (
            <div className="absolute top-2 left-2 bg-black/60 text-white text-[10px] px-2 py-1 rounded">
              Loading wind…
            </div>
          )}
        </div>
        <WindLegend />
      </div>
    </section>
  );
}

function WindLegend() {
  // 9 bands: 0-5, 5-10, ..., 40-45+. Each band is filled with the color at
  // the midpoint of its range, so the visible swatch accurately represents
  // that speed band (not a smooth gradient that makes it hard to map a
  // color back to a specific speed).
  const boundaries = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45];
  const bands = [];
  for (let i = 0; i < boundaries.length - 1; i++) {
    const lo = boundaries[i];
    const hi = boundaries[i + 1];
    const mid = (lo + hi) / 2;
    bands.push({ lo, hi, mid, color: colorForMph(mid) });
  }
  return (
    <div className="bg-black/70 px-3 py-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-white/70">
          Wind speed (mph)
        </span>
        <span className="text-[10px] text-white/50">10 m above ground</span>
      </div>

      {/* Colored stepped bands */}
      <div className="flex rounded-sm overflow-hidden h-3.5 border border-white/10">
        {bands.map((b) => (
          <div
            key={b.lo}
            className="flex-1"
            style={{ background: b.color }}
            title={`${b.lo}–${b.hi}${b.hi === 45 ? '+' : ''} mph`}
          />
        ))}
      </div>

      {/* Tick labels at each 5 mph boundary */}
      <div className="relative h-4 mt-0.5">
        {boundaries.map((b, i) => (
          <span
            key={b}
            className="absolute text-[10px] text-white/75 tabular-nums"
            style={{
              left: `${(i / (boundaries.length - 1)) * 100}%`,
              transform: 'translateX(-50%)',
            }}
          >
            {b}
            {b === 45 ? '+' : ''}
          </span>
        ))}
      </div>
    </div>
  );
}
